<template>
  <div class="product-details m-bot-3">
    <!-- Mobile Intro Section -->
    <div class="product-details__mobile-intro hide-for-large">
      <PagesProductBreadcrumbs
        :categories="categories"
        :subcategories="subcategories"
        :typeHandle="typeHandle"
      />
      <div v-if="product.title" class="title-section">
        <h1 class="title-big2" v-html="product.title" />
      </div>
      <div class="info-section text-small3 is-medium-bold">
        <div
          v-if="product.sezioneBio && product.sezioneBio.length"
          class="bio-section"
        >
          <span
            v-for="(item, index) in product.sezioneBio"
            :key="index"
            v-html="$t('product.' + item)"
          />
        </div>
      </div>
    </div>
    <!-- Product Image Section -->
    <div class="product-details__image">
      <div
        v-if="product.portrait && product.portrait.length"
        class="product-image"
      >
        <WidgetPicture
          :image="{
            portrait: product.portrait[0],
          }"
          :alt="product.title"
        />
      </div>
      <div class="product-buttons">
        <div class="btns-container">
          <a
            v-show="product.linkAcquista && showAcquistaLink"
            class="round-icon-btn is-filled"
            :href="product.linkAcquista"
            target="_blank"
          >
            <span
              class="text-small4 is-medium-bold"
              v-html="$t('common.buy')"
            />
          </a>
          <nuxt-link
            class="round-icon-btn is-default"
            :to="
              localePath({
                name: 'contatti-product',
                params: { product: product.slug },
              })
            "
          >
            <span
              class="text-small4 is-medium-bold"
              v-html="$t('common.richiediInfo')"
            />
          </nuxt-link>
        </div>
      </div>
    </div>

    <!-- Product Intro Section -->
    <div class="product-details__intro">
      <div class="product-details__intro__wrap">
        <section class="show-for-large">
          <PagesProductBreadcrumbs
            :categories="categories"
            :subcategories="subcategories"
            :typeHandle="typeHandle"
          />
          <div v-if="product.title" class="title-section">
            <h1 class="title-big2" v-html="product.title" />
          </div>
          <div class="info-section text-small3 is-medium-bold">
            <div
              v-if="product.sezioneBio && product.sezioneBio.length"
              class="bio-section"
            >
              <span
                v-for="(item, index) in product.sezioneBio"
                :key="index"
                v-html="$t('product.' + item)"
              />
            </div>
          </div>
        </section>

        <!-- Product Description -->
        <div class="product-description" v-if="localizedDescrizioneProdotto">
          <div class="text-big" v-html="localizedDescrizioneProdotto" />
        </div>

        <!-- Stili / Adatto Per Section -->
        <div
          class="stili-adatto-per text-medium"
          v-if="filtroList && filtroList.length"
        >
          <p class="text-medium">{{ label }}:&nbsp;&nbsp;</p>
          <p
            v-for="(item, index) in filtroList"
            :key="index"
            class="text-base item"
          >
            {{ formattedItemTitle(item)
            }}<span v-if="index < filtroList.length - 1">,&nbsp;</span>
          </p>
        </div>

        <!-- Icons Section -->
        <div class="icons" v-if="product.icone && product.icone.length">
          <div v-for="(icon, index) in product.icone" :key="index" class="icon">
            <component :is="getIconComponent(icon).component" :filled="true" />
            <span class="text-small">{{ getIconComponent(icon).text }}</span>
          </div>
        </div>

        <!-- Formati Section -->
        <div
          class="formati-section"
          v-if="
            product.disponibilitaFormatoProdotto &&
            product.disponibilitaFormatoProdotto.length
          "
        >
          <p class="text-medium">
            {{ $t('product.disponibilitaProdotto') }}
            <span
              v-for="(formato, index) in product.disponibilitaFormatoProdotto"
              :key="index"
              class="formati-section__formato"
            >
              <img
                v-if="formato.image.length"
                :src="formato.image[0].url"
                :alt="formato.title"
              />
              <span v-if="formato.title" class="text-base is-normal-weight">
                {{ formato.title
                }}<span
                  v-if="index < product.disponibilitaFormatoProdotto.length - 1"
                  >,&nbsp;</span
                >
              </span>
            </span>
          </p>
        </div>

        <!-- PDF Section -->
        <div
          v-if="localizedSchedaTecnica && localizedSchedaTecnica[0]"
          class="product-pdf m-top-0"
        >
          <a
            class="round-icon-btn with-icon is-default"
            :href="localizedSchedaTecnica[0].url"
            target="_blank"
            download
          >
            <span class="text-small4 is-medium-bold" v-html="downloadLabel" />
            <DownloadIcon alt="Download icon" class="icon-arrow" />
          </a>
        </div>

        <!-- Accordion - Applicazioni/Specifiche Tecniche/Preparazione -->
        <PagesProductAccordion
          :applicazioni="localizedApplicazioni"
          :specifiche="localizedSpecificheTecniche"
          :preparazione="product.modalitaDuso"
        />

        <!-- Etichetta ambientale -->
        <PagesProductEtichettaAmbientale
          v-if="product.smaltimento"
          :smaltimento="product.smaltimento"
          :typeHandle="typeHandle"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import DownloadIcon from '@/assets/img/ico/download-black.svg'
import OgmFreeSvg from '@/assets/img/ico/icon_ogm.svg'
import AllergenFreeSvg from '@/assets/img/ico/icon_allergen.svg'
import { useProductDetails } from '~/composables/product-details.js'

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
})

const { t } = useI18n()

// Vino adatto per / Stili birra - Categoria Vino / Categoria Lievito Birra
const {
  typeHandle,
  filtroTipologiaVino,
  filtroStiliBirra,
  filtroCategoriaProdottoVino,
  filtroCategoriaLievitoBirra,
  filtroCategoriaLievitoVino,
} = props.product

const { filtroList, itemPrefix, label, subcategories, categories } =
  useProductDetails({
    typeHandle,
    filtroTipologiaVino,
    filtroStiliBirra,
    filtroCategoriaProdottoVino,
    filtroCategoriaLievitoBirra,
    filtroCategoriaLievitoVino,
  })

// OGM - Allergen icons
const iconComponents = {
  ogmFree: { component: OgmFreeSvg, text: 'OGM FREE' },
  allergenFree: { component: AllergenFreeSvg, text: 'ALLERGEN FREE' },
}
const getIconComponent = (icon) => {
  return iconComponents[icon] || { component: null, text: '' }
}

// Traduzioni per campi-testo del prodotto in base alla lingua
const { locale } = useI18n()
const getLocalizedField = (field) => {
  switch (locale.value) {
    case 'en':
      return props.product[`${field}En`]
    case 'es':
      return props.product[`${field}Es`]
    case 'fr':
      return props.product[`${field}Fr`]
    default:
      return props.product[field]
  }
}

const localizedDescrizioneProdotto = computed(() =>
  getLocalizedField('descrizioneProdotto'),
)
const localizedSpecificheTecniche = computed(() =>
  getLocalizedField('specificheTecniche'),
)
const localizedApplicazioni = computed(() => getLocalizedField('applicazioni'))
const localizedSchedaTecnica = computed(() =>
  getLocalizedField('schedaTecnica'),
)

const showAcquistaLink = computed(() => {
  return locale.value === 'it'
})

const formattedItemTitle = (item) => {
  return locale.value === 'en'
    ? `${item.title} ${itemPrefix.value}`
    : `${itemPrefix.value} ${item.title}`
}

const downloadLabel = computed(() => {
  if (typeHandle === 'prodottoPanificati') {
    return t('common.scaricaSchedaPanificati')
  }
  return t('common.scaricaScheda')
})
</script>

<script>
export default {
  name: 'ProductDetails',
}
</script>

<style lang="scss">
@import './style.scss';
</style>
